/* eslint-disable react/sort-comp */
import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import type {
  IManagedFocuserInjectedProps,
  IManagedFocuserExternalProps,
  IFocusableRef,
} from './types';

interface ManagedFocuserRuntimeFields {
  instanceRef: IFocusableRef | null;
}

/**
 * @param {React.Component} ComponentToWrap - component to be enhanced
 * @returns {React.Component} enhanced version of the argument component
 */
export const WithManagedFocuser = <P extends object>(ComponentToWrap: React.ComponentType<P & IManagedFocuserInjectedProps>): React.ComponentType<P & IManagedFocuserExternalProps> => {
  class HoC extends React.PureComponent<P & IManagedFocuserExternalProps> {
    static displayName = `WithManagedFocuser(${getDisplayName(ComponentToWrap)})`;

    constructor(props: P & IManagedFocuserExternalProps) {
      super(props);

      const hoc = this as this & ManagedFocuserRuntimeFields;
      hoc.instanceRef = null;
    }

    componentDidMount() {
      if (this.props.isFocused) {
        this.focusThis();
      }
    }

    componentDidUpdate() {
      if (this.props.isFocused) {
        this.focusThis();
      }
    }

    focusableRef = (ref: IFocusableRef | null) => {
      const hoc = this as this & ManagedFocuserRuntimeFields;
      hoc.instanceRef = ref;
    };

    focusThis = () => {
      const hoc = this as this & ManagedFocuserRuntimeFields;
      (hoc.instanceRef as IFocusableRef).focus();
    };

    render() {
      return (
        <ComponentToWrap
          focusableRef={this.focusableRef}
          focusThis={this.focusThis}
          {...this.props}
        />
      );
    }
  }

  return hoistNonReactStatics(HoC, ComponentToWrap);
};
